import { useState } from "react";
import { BBOT_DJANGO_SERVER } from "Config";
import { OPContainer } from "top-component-library";
import { BOS_SETTINGS_ROUTE, LEGACY_CONSOLE_PATH } from "./constants";
import HostnameConfigDetail from "./detail/HostnameConfigDetail";
import HostnameConfigList from "./HostnameConfigList";
import { LegacyLinkAlert } from "./styles";
import { useHostnameConfigs } from "./useHostnameConfigs";
import { useHostnameLocations } from "./useHostnameLocations";
import { isAdminUser, isBbotAdminUser } from "./utils";

const BosSettings = ({ selectedCustomer, userInfo }) => {
  const isAdmin = isAdminUser(userInfo);
  const isBbotAdmin = isBbotAdminUser(userInfo);
  const {
    hostnameConfigs,
    isLoading,
    updateHostnameConfig,
    enableLegacyBos,
    addHostname,
    addHostnameFromPrompt,
    archiveHostname,
    bulkDeleteHostnames,
    enableBasicDeliveryTheme,
    temporarilyDisableOrdering,
    fetchTemporaryDisableText,
  } = useHostnameConfigs(selectedCustomer, userInfo);
  const {
    locationsByCustomer,
    locationCustomers,
    fulfillmentMethods,
    prettyNames,
    isLoadingLocations,
    isSavingLocations,
    saveHostnameToLocations,
  } = useHostnameLocations(selectedCustomer, isBbotAdmin);
  const [selectedHostname, setSelectedHostname] = useState(null);

  // Re-derived from the latest hostnameConfigs every render (rather than held as
  // its own copy) so a save's post-refresh data shows up immediately.
  const selectedConfig = hostnameConfigs.find((config) => config.hostname === selectedHostname) ?? null;

  const legacyLink = BBOT_DJANGO_SERVER + (isAdmin ? "/superuser/console" : "/owner/console") + LEGACY_CONSOLE_PATH;
  const customerName = isAdmin ? selectedCustomer?.name_for_admin : selectedCustomer?.name_for_owner;

  return (
    <OPContainer title="BOS Settings" url={BOS_SETTINGS_ROUTE} selectedCustomer={selectedCustomer} userInfo={userInfo}>
      <div className="margin-bottom-2">
        <LegacyLinkAlert
          message={
            <span>
              We&apos;ve updated this page! We&apos;ll retire the old page soon but you can view it{" "}
              <a href={legacyLink}>here.</a>
            </span>
          }
        />
      </div>
      {/* Mirrors edit-hostname-configs.html's list-only <h2><small> tip. Its middle
          sentence was ng-if="mactrl.is_super_user", which views_for_owners.py sets
          to isBbotAdmin. */}
      {!selectedHostname && (
        <>
          <p>
            View and manage your Branded Ordering Site (<strong>BOS</strong>) domains.{" "}
            {isBbotAdmin && (
              <>
                Delivery Locations for {customerName} will be accessible from the domain names listed below. Choosing a
                domain name from the list will allow you to view its configuration and settings.
              </>
            )}
          </p>
          <p>
            <strong>
              <i>Note: domains that are greyed out are owned by a parent account and are read only.</i>
            </strong>
          </p>
        </>
      )}
      {selectedHostname ? (
        <HostnameConfigDetail
          hostnameConfig={selectedConfig}
          isAdmin={isAdmin}
          isBbotAdmin={isBbotAdmin}
          customerName={customerName}
          onBack={() => setSelectedHostname(null)}
          onSave={updateHostnameConfig}
          onEnableLegacyBos={enableLegacyBos}
          onArchiveHostname={archiveHostname}
          onEnableBasicDeliveryTheme={enableBasicDeliveryTheme}
          onEnableWhiteLabelBuilder={updateHostnameConfig}
          onTemporarilyDisableOrdering={temporarilyDisableOrdering}
          fetchTemporaryDisableText={fetchTemporaryDisableText}
          locationsByCustomer={locationsByCustomer}
          locationCustomers={locationCustomers}
          fulfillmentMethods={fulfillmentMethods}
          prettyNames={prettyNames}
          isLoadingLocations={isLoadingLocations}
          isSavingLocations={isSavingLocations}
          onSaveHostnameLocations={saveHostnameToLocations}
        />
      ) : (
        <HostnameConfigList
          hostnameConfigs={hostnameConfigs}
          isLoading={isLoading}
          isAdmin={isAdmin}
          customerName={customerName}
          onSelectHostname={setSelectedHostname}
          onAddHostname={addHostname}
          onAddHostnameFromPrompt={addHostnameFromPrompt}
          onBulkDeleteHostnames={bulkDeleteHostnames}
        />
      )}
    </OPContainer>
  );
};

export default BosSettings;
